import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  CssBaseline,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  IconButton,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Alert,
} from '@mui/material';
import styled from 'styled-components';
import CircularProgress from '@mui/material/CircularProgress';
import { blue, grey } from '@mui/material/colors';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { breadcrumbsActions } from '@store/breadcrumbs';
import JsonView from 'react-json-view';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router';
import { updateConfigurationModule } from '../../legacy-payment-options.breadcrumbs';
import {
  getLegacyConfigModuleById,
  rollBackLegacyConfigModuleByFilename,
  selectSelectedConfigModule,
  selectSelectedLastModifiedBy,
  updateLegacyConfigModuleById,
} from '../../store/legacy-payment-options-configuration-module';

const LoaderContainer = styled.div`
  display: flex;
  min-height: calc(100vh - 205px);
  flex-direction: column;
  padding: 0 10px 20px 10px;
  background: white;
`;

const CircularProgressWrapper = styled.div`
  margin: 0px auto;
  margin-top: 300px;
`;

export const REACT_JSON_PROPS = {
  theme: 'bright',
  indentWidth: 6,
  collapsed: 2,
  editable: true,
  quotesOnKeys: false,
  displayDataTypes: true,
};

const getParentFromPath = (dataCopy, path) => {
  if (path.length === 0) return dataCopy;
  let parent = dataCopy;
  const parentPath = path.slice(0, -1);
  parent = parentPath.reduce((acc, key) => acc[key], dataCopy);
  return parent;
};

// eslint-disable-next-line react/function-component-definition
const EditableValue = ({ value, path, onUpdate }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(String(value));
  const [originalType, setOriginalType] = useState(typeof value);

  useEffect(() => {
    setCurrentValue(String(value));
    setOriginalType(typeof value);
  }, [value]);

  const handleEditClick = () => {
    setOriginalType(typeof value);
    setCurrentValue(String(value));
    setIsEditing(true);
  };

  const handleSave = () => {
    let newValue;

    switch (originalType) {
      case 'string':
        newValue = currentValue;
        break;

      case 'number':
        newValue = parseFloat(currentValue) || 0;
        break;

      default:
        try {
          newValue = JSON.parse(currentValue);
        } catch (e) {
          newValue = currentValue;
        }
    }

    onUpdate(path, newValue);
    setIsEditing(false);
  };

  const handleNumberChange = (e) => {
    const regex = /^-?([0-9]+)?\.?([0-9]+)?$/;
    if (
      e.target.value === '' ||
      e.target.value === '-' ||
      regex.test(e.target.value)
    ) {
      setCurrentValue(e.target.value);
    }
  };

  if (isEditing) {
    return (
      <TextField
        value={currentValue}
        onChange={
          originalType === 'number'
            ? handleNumberChange
            : (e) => setCurrentValue(e.target.value)
        }
        onBlur={handleSave}
        onKeyDown={(e) => {
          if (e.key === 'Enter') handleSave();
          if (e.key === 'Escape') setIsEditing(false);
        }}
        autoFocus
        size="small"
        variant="outlined"
        sx={{ width: '100%', bgcolor: 'white' }}
      />
    );
  }

  const displayValue = String(value);
  const chipProps = {
    label: displayValue,
    size: 'small',
    variant: 'outlined',
    onClick: handleEditClick,
    sx: { cursor: 'pointer', fontFamily: 'monospace' },
  };

  if (typeof value === 'string') {
    if (value === '') {
      chipProps.label = '"" (Empty)';
      chipProps.color = 'default';
      chipProps.sx = { ...chipProps.sx, fontStyle: 'italic' };
    } else {
      chipProps.label = `"${displayValue}"`;
      chipProps.color = 'success';
    }
  } else if (typeof value === 'number') {
    chipProps.color = 'primary';
  } else if (typeof value === 'boolean') {
    chipProps.color = 'secondary';
  } else if (value === null || value === undefined) {
    chipProps.label = value === null ? 'null' : 'undefined';
    chipProps.color = 'default';
    chipProps.sx = { ...chipProps.sx, fontStyle: 'italic' };
  }

  // eslint-disable-next-line react/jsx-props-no-spreading
  return <Chip {...chipProps} />;
};

// eslint-disable-next-line react/function-component-definition
const AddArrayEntry = ({ path, onAdd }) => {
  const [value, setValue] = useState('');
  const [type, setType] = useState('String');

  const handleSubmit = (e) => {
    e.preventDefault();

    let newValue;
    switch (type) {
      case 'Number':
        newValue = parseFloat(value) || 0;
        break;
      case 'Boolean':
        newValue = value.toLowerCase() === 'true';
        break;
      case 'Object':
        newValue = {};
        break;
      case 'Array':
        newValue = [];
        break;
      case 'String':
      default:
        newValue = value;
    }

    onAdd(path, null, newValue);
    setValue('');
    setType('String');
  };

  return (
    <TableRow>
      <TableCell
        colSpan={3}
        sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <FormControl
            size="small"
            sx={{ minWidth: 120 }}>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}>
              <MenuItem value="String">String</MenuItem>
              <MenuItem value="Number">Number</MenuItem>
              <MenuItem value="Boolean">Boolean</MenuItem>
              <MenuItem value="Object">Object</MenuItem>
              <MenuItem value="Array">Array</MenuItem>
            </Select>
          </FormControl>
          {(type === 'String' || type === 'Number' || type === 'Boolean') && (
            <TextField
              label="New Value"
              variant="outlined"
              size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              sx={{ flexGrow: 1, minWidth: '120px', maxWidth: '400px' }}
              type={type === 'Number' ? 'number' : 'text'}
              placeholder={type === 'Boolean' ? 'true / false' : ''}
            />
          )}
          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            size="medium">
            Add Item
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

// eslint-disable-next-line react/function-component-definition
const AddObjectEntry = ({ path, onAdd }) => {
  const [key, setKey] = useState('');
  const [value, setValue] = useState('');
  const [type, setType] = useState('String');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!key) return;

    let newValue;
    switch (type) {
      case 'Number':
        newValue = parseFloat(value) || 0;
        break;
      case 'Boolean':
        newValue = value.toLowerCase() === 'true';
        break;
      case 'Object':
        newValue = {};
        break;
      case 'Array':
        newValue = [];
        break;
      case 'String':
      default:
        newValue = value;
    }

    onAdd(path, key, newValue);
    setKey('');
    setValue('');
    setType('String');
  };

  return (
    <TableRow>
      <TableCell
        colSpan={3}
        sx={{ p: 1.5, borderTop: 1, borderColor: 'divider' }}>
        <Box
          component="form"
          onSubmit={handleSubmit}
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1.5,
            alignItems: 'center',
          }}>
          <TextField
            label="New Key"
            variant="outlined"
            size="small"
            value={key}
            onChange={(e) => setKey(e.target.value)}
            sx={{ flexGrow: 1, minWidth: '120px' }}
            required
          />
          <FormControl
            size="small"
            sx={{ minWidth: 120 }}>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value)}>
              <MenuItem value="String">String</MenuItem>
              <MenuItem value="Number">Number</MenuItem>
              <MenuItem value="Boolean">Boolean</MenuItem>
              <MenuItem value="Object">Object</MenuItem>
              <MenuItem value="Array">Array</MenuItem>
            </Select>
          </FormControl>
          {(type === 'String' || type === 'Number' || type === 'Boolean') && (
            <TextField
              label="New Value"
              variant="outlined"
              size="small"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              sx={{ flexGrow: 1, minWidth: '120px' }}
              type={type === 'Number' ? 'number' : 'text'}
            />
          )}
          <Button
            type="submit"
            variant="contained"
            color="success"
            startIcon={<AddIcon />}
            size="medium">
            Add
          </Button>
        </Box>
      </TableCell>
    </TableRow>
  );
};

// eslint-disable-next-line react/function-component-definition
const RecursiveJsonEditor = ({ data, path, onUpdate, onDelete, onAdd }) => {
  if (Array.isArray(data)) {
    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ my: 1, borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: blue[50] }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  width: '15%',
                }}>
                Index
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                Value
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  color: 'primary.main',
                  width: '80px',
                }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => {
              const currentPath = [...path, index];
              return (
                <TableRow
                  key={JSON.stringify(item)}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      bgcolor: blue[50],
                      verticalAlign: 'top',
                      fontFamily: 'monospace',
                    }}>
                    {index}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', p: 1 }}>
                    <RecursiveJsonEditor
                      data={item}
                      path={currentPath}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onAdd={onAdd}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ verticalAlign: 'top' }}>
                    <IconButton
                      onClick={() => onDelete(currentPath)}
                      size="small"
                      color="error"
                      title="Delete Item">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter sx={{ bgcolor: blue[50] }}>
            <AddArrayEntry
              path={path}
              onAdd={onAdd}
            />
          </TableFooter>
        </Table>
      </TableContainer>
    );
  }

  if (typeof data === 'object' && data !== null) {
    return (
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ my: 1, borderRadius: 2 }}>
        <Table size="small">
          <TableHead sx={{ bgcolor: grey[100] }}>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  width: '30%',
                }}>
                Key
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', color: 'text.secondary' }}>
                Value
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  fontWeight: 'bold',
                  color: 'text.secondary',
                  width: '80px',
                }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Object.entries(data).map(([key, value]) => {
              const currentPath = [...path, key];
              return (
                <TableRow
                  key={key}
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{
                      bgcolor: grey[100],
                      verticalAlign: 'top',
                      fontFamily: 'monospace',
                      fontWeight: 500,
                    }}>
                    {key}
                  </TableCell>
                  <TableCell sx={{ verticalAlign: 'top', p: 1 }}>
                    <RecursiveJsonEditor
                      data={value}
                      path={currentPath}
                      onUpdate={onUpdate}
                      onDelete={onDelete}
                      onAdd={onAdd}
                    />
                  </TableCell>
                  <TableCell
                    align="center"
                    sx={{ verticalAlign: 'top' }}>
                    <IconButton
                      onClick={() => onDelete(currentPath)}
                      size="small"
                      color="error"
                      title="Delete Key">
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
          <TableFooter sx={{ bgcolor: grey[100] }}>
            <AddObjectEntry
              path={path}
              onAdd={onAdd}
            />
          </TableFooter>
        </Table>
      </TableContainer>
    );
  }

  return (
    <EditableValue
      value={data}
      path={path}
      onUpdate={onUpdate}
    />
  );
};

export default function App({ permissions }) {
  const [jsonData, setJsonData] = useState([]);
  const [isFetchingData, setIsFetchingData] = useState(true);
  const [formSuccessMsg, setFormSuccessMsg] = useState('');
  const [formErrorMsg, setFormErrorMsg] = useState('');
  const dispatch = useDispatch();
  const { filePath } = useParams();
  const [isEdit, setIsEditMode] = useState(filePath !== undefined);
  const selectedConfigModule = useSelector(selectSelectedConfigModule);
  const selectedLastModifiedBy = useSelector(selectSelectedLastModifiedBy);
  const navigate = useNavigate();

  useEffect(() => {
    setIsEditMode(filePath !== undefined);
  }, [filePath]);

  useEffect(() => {
    setIsFetchingData(true);
    dispatch(breadcrumbsActions.showMenu(true));
    dispatch(
      breadcrumbsActions.setBreadcrumbs(updateConfigurationModule(filePath)),
    );
    Promise.all([dispatch(getLegacyConfigModuleById(filePath))]).then(() =>
      setIsFetchingData(false),
    );
  }, [dispatch, filePath, isEdit]);

  // Authorization
  useEffect(() => {
    if (!permissions?.canViewConfigModule) {
      navigate('/landing');
    }
  }, [navigate, permissions]);

  useEffect(() => {
    if (isEdit) {
      setJsonData(selectedConfigModule);
    } else {
      setJsonData(null);
    }
  }, [filePath, isEdit, selectedConfigModule]);

  const handleUpdate = (path, value) => {
    setJsonData((currentData) => {
      const dataCopy = JSON.parse(JSON.stringify(currentData));
      const parent = getParentFromPath(dataCopy, path);
      const finalKey = path[path.length - 1];
      parent[finalKey] = value;
      return dataCopy;
    });
  };

  const handleDelete = (path) => {
    setJsonData((currentData) => {
      const dataCopy = JSON.parse(JSON.stringify(currentData));
      const parent = getParentFromPath(dataCopy, path);
      const finalKey = path[path.length - 1];

      if (Array.isArray(parent)) {
        parent.splice(finalKey, 1);
      } else if (typeof parent === 'object' && parent !== null) {
        delete parent[finalKey];
      }
      return dataCopy;
    });
  };

  const handleAdd = (path, key, value) => {
    setJsonData((currentData) => {
      const dataCopy = JSON.parse(JSON.stringify(currentData));
      let parent = dataCopy;
      if (path.length > 0) {
        parent = path.reduce((acc, k) => acc[k], dataCopy);
      }

      if (Array.isArray(parent)) {
        // UPDATED LOGIC:
        // Use 'value' (the 3rd arg) to push to array.
        // Previously, your code pushed 'key' which is inconsistent for generic adding.
        parent.push(value);
      } else if (typeof parent === 'object' && parent !== null) {
        if (key) parent[key] = value;
      }
      return dataCopy;
    });
  };

  const onSubmit = () => {
    if (!permissions?.canEditConfigModule) {
      return;
    }
    const payload = {
      filePath: filePath.replace(/_/g, '/'),
      data: jsonData,
    };

    setFormSuccessMsg('');
    setFormErrorMsg('');

    dispatch(updateLegacyConfigModuleById({ payload })).then(
      ({ payload: response }) => {
        if (response && response.status === 'SUCCESS') {
          setFormSuccessMsg(response.message);
        } else {
          setFormErrorMsg(
            response?.message || `Unable to update config module`,
          );
        }
      },
    );
  };

  const onSubmitRollback = () => {
    if (!permissions?.canRollBackConfigModule) {
      return;
    }
    const payload = {
      filePath: filePath.replace(/_/g, '/'),
    };

    setFormSuccessMsg('');
    setFormErrorMsg('');

    dispatch(rollBackLegacyConfigModuleByFilename({ payload })).then(
      ({ payload: response }) => {
        if (response && response.status === 'SUCCESS') {
          setFormSuccessMsg(response.message);
          setIsFetchingData(true);
          Promise.all([dispatch(getLegacyConfigModuleById(filePath))]).then(
            () => setIsFetchingData(false),
          );
        } else {
          setFormErrorMsg(
            response?.message || `Unable to rollback config module`,
          );
        }
      },
    );
  };

  if (isFetchingData) {
    return (
      <LoaderContainer>
        <CircularProgressWrapper>
          <CircularProgress />
        </CircularProgressWrapper>
      </LoaderContainer>
    );
  }

  return (
    <div>
      {jsonData && !isFetchingData && (
        <div>
          <CssBaseline />
          <Container
            maxWidth="xl"
            sx={{
              py: { xs: 2, sm: 4 },
              bgcolor: 'grey.50',
              minHeight: '100vh',
            }}>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
              Last Modified By: {selectedLastModifiedBy}
            </Typography>
            <Typography
              variant="h4"
              component="h1"
              gutterBottom
              sx={{
                fontWeight: 'bold',
                pb: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}>
              Filepath: {filePath.replace(/_/g, '/')}
            </Typography>
            <Paper
              elevation={3}
              sx={{ p: { xs: 1, sm: 2, md: 3 }, borderRadius: 3, mb: 4 }}>
              <RecursiveJsonEditor
                data={jsonData}
                path={[]}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
                onAdd={handleAdd}
              />
            </Paper>

            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{ fontWeight: 'semibold' }}>
              Live JSON Preview
            </Typography>
            <Paper
              component="pre"
              elevation={0}
              variant="outlined"
              sx={{
                bgcolor: 'black',
                color: 'success.light',
                p: 2,
                borderRadius: 2,
                overflow: 'auto',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
              }}>
              <JsonView
                src={jsonData}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...REACT_JSON_PROPS}
              />
            </Paper>
            {formSuccessMsg && (
              <Alert
                onClose={() => {
                  setFormSuccessMsg('');
                }}
                severity="success"
                sx={{ mt: 2 }}>
                {formSuccessMsg}
              </Alert>
            )}
            {formErrorMsg && (
              <Alert
                onClose={() => {
                  setFormErrorMsg('');
                }}
                severity="error"
                sx={{ mt: 2 }}>
                {formErrorMsg}
              </Alert>
            )}
            <Button
              onClick={() => onSubmit()}
              id="submitBtn"
              style={{ marginTop: '32px' }}
              fullWidth
              size="large"
              variant="contained"
              type="submit">
              Update
            </Button>
            <Button
              onClick={() => onSubmitRollback()}
              id="submitBtn"
              style={{ marginTop: '32px' }}
              fullWidth
              size="large"
              variant="contained"
              type="submit">
              Rollback to previous version
            </Button>
          </Container>
        </div>
      )}
      {!jsonData && !isFetchingData && (
        <div>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 'bold',
              pb: 1,
              borderBottom: 1,
              borderColor: 'divider',
            }}>
            Filepath: {filePath.replace(/_/g, '/')}
          </Typography>
          <h4 style={{ color: 'red' }}>
            Unable to load file. Please try again later
          </h4>
        </div>
      )}
    </div>
  );
}
